import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DisclaimerBanner } from "@/components/DisclaimerBanner";
import { CandleChart } from "@/components/CandleChart";
import { TrendingUp, TrendingDown, RotateCcw, X, Wallet } from "lucide-react";

export default function Simulator() {
  const { setProgress, loadProgress } = useAuth();
  const [symbols, setSymbols] = useState({});
  const [account, setAccount] = useState(null);
  const [history, setHistory] = useState(null);
  const [market, setMarket] = useState("crypto");
  const [symbol, setSymbol] = useState("BTC");
  const [side, setSide] = useState("long");
  const [qty, setQty] = useState(0.1);
  const [sl, setSl] = useState("");
  const [tp, setTp] = useState("");
  const [placing, setPlacing] = useState(false);
  const [chartData, setChartData] = useState(null);

  const loadAccount = useCallback(async () => {
    try { const { data } = await api.get("/sim/account"); setAccount(data); } catch {}
  }, []);
  const loadHistory = useCallback(async () => {
    try { const { data } = await api.get("/sim/history"); setHistory(data); } catch {}
  }, []);

  useEffect(() => {
    api.get("/market/symbols").then(({ data }) => setSymbols(data)).catch(() => {});
    loadAccount();
    loadHistory();
    const iv = setInterval(loadAccount, 4000);
    return () => clearInterval(iv);
  }, [loadAccount, loadHistory]);

  useEffect(() => {
    let active = true;
    const loadChart = async () => {
      try {
        const { data } = await api.get("/market/candles", { params: { symbol, timeframe: "1h", market } });
        if (active) setChartData(data);
      } catch { if (active) setChartData(null); }
    };
    loadChart();
    const iv = setInterval(loadChart, 15000);
    return () => { active = false; clearInterval(iv); };
  }, [symbol, market]);

  const onMarket = (m) => { setMarket(m); const f = symbols[m]?.[0]?.symbol; if (f) setSymbol(f); };

  const placeOrder = async () => {
    setPlacing(true);
    try {
      await api.post("/sim/order", {
        symbol, market, side, quantity: parseFloat(qty),
        stop_loss: sl ? parseFloat(sl) : null, take_profit: tp ? parseFloat(tp) : null,
      });
      toast.success(`${side === "long" ? "Long" : "Short"} position opened`);
      setSl(""); setTp("");
      loadAccount();
      loadProgress();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Order failed");
    } finally { setPlacing(false); }
  };

  const closePosition = async (id) => {
    try {
      const { data } = await api.post(`/sim/close/${id}`);
      toast[data.pnl >= 0 ? "success" : "error"](`Closed · PnL ${data.pnl >= 0 ? "+" : ""}$${data.pnl}`);
      loadAccount();
      loadHistory();
    } catch { toast.error("Could not close position"); }
  };

  const reset = async () => {
    await api.post("/sim/reset");
    toast.success("Account reset to $100,000");
    loadAccount(); loadHistory();
  };

  const fmt = (n) => Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const chartLast = chartData?.candles?.[chartData.candles.length - 1];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Paper Trading Simulator</h1>
          <p className="text-slate-400 mt-1">Practice long & short trades with $100,000 virtual money.</p>
        </div>
        <button onClick={reset} data-testid="sim-reset" className="inline-flex items-center gap-2 text-sm bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg px-4 py-2 transition-colors">
          <RotateCcw className="w-4 h-4" /> Reset account
        </button>
      </div>

      <DisclaimerBanner />

      {/* Account summary */}
      {account && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            ["Cash", `$${fmt(account.cash)}`, "text-slate-100"],
            ["Equity", `$${fmt(account.equity)}`, "text-blue-400"],
            ["Unrealized PnL", `${account.unrealized_pnl >= 0 ? "+" : ""}$${fmt(account.unrealized_pnl)}`, account.unrealized_pnl >= 0 ? "text-bull" : "text-bear"],
            ["Total Return", `${account.total_return_pct >= 0 ? "+" : ""}${account.total_return_pct}%`, account.total_return_pct >= 0 ? "text-bull" : "text-bear"],
          ].map(([label, val, color]) => (
            <div key={label} className="bg-[#0F172A] border border-slate-800 rounded-2xl p-5">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-mono mb-1">{label}</div>
              <div className={`font-mono text-xl sm:text-2xl font-bold ${color}`} data-testid={`sim-${label.toLowerCase().replace(/\s+/g, "-")}`}>{val}</div>
            </div>
          ))}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Order form */}
        <div className="bg-[#0F172A] border border-slate-800 rounded-2xl p-5 space-y-4 h-fit">
          <div className="flex items-center gap-2"><Wallet className="w-4 h-4 text-blue-400" /><h2 className="font-display font-semibold">New Order</h2></div>
          <div className="flex flex-wrap gap-1.5">
            {["crypto", "stocks", "forex", "indices", "commodities"].map((m) => (
              <button key={m} data-testid={`sim-market-${m}`} onClick={() => onMarket(m)} className={`px-2.5 py-1 rounded-md text-xs font-medium capitalize transition-colors ${market === m ? "bg-blue-600 text-white" : "bg-slate-800/60 text-slate-400"}`}>{m}</button>
            ))}
          </div>
          <div className="space-y-1.5">
            <label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Symbol</label>
            <select data-testid="sim-symbol" value={symbol} onChange={(e) => setSymbol(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm font-mono focus:border-blue-500 focus:outline-none">
              {(symbols[market] || []).map((s) => <option key={s.symbol} value={s.symbol}>{s.symbol} — {s.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="sim-side-long" onClick={() => setSide("long")} className={`flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${side === "long" ? "bg-emerald-600 text-white" : "bg-slate-800/60 text-slate-400"}`}><TrendingUp className="w-4 h-4" /> Long</button>
            <button data-testid="sim-side-short" onClick={() => setSide("short")} className={`flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${side === "short" ? "bg-red-600 text-white" : "bg-slate-800/60 text-slate-400"}`}><TrendingDown className="w-4 h-4" /> Short</button>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Quantity</label>
            <input data-testid="sim-qty" type="number" step="any" value={qty} onChange={(e) => setQty(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm font-mono focus:border-blue-500 focus:outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Stop Loss</label>
              <input data-testid="sim-sl" type="number" step="any" value={sl} onChange={(e) => setSl(e.target.value)} placeholder="optional" className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm font-mono focus:border-blue-500 focus:outline-none placeholder:text-slate-600" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Take Profit</label>
              <input data-testid="sim-tp" type="number" step="any" value={tp} onChange={(e) => setTp(e.target.value)} placeholder="optional" className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm font-mono focus:border-blue-500 focus:outline-none placeholder:text-slate-600" />
            </div>
          </div>
          <button data-testid="sim-place-order" onClick={placeOrder} disabled={placing} className={`w-full font-semibold rounded-lg py-2.5 text-white transition-colors disabled:opacity-60 ${side === "long" ? "bg-emerald-600 hover:bg-emerald-500" : "bg-red-600 hover:bg-red-500"}`}>
            {placing ? "Placing…" : `Open ${side} position`}
          </button>
        </div>

        {/* Positions & history */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-[#0F172A] border border-slate-800 rounded-2xl overflow-hidden" data-testid="sim-chart-card">
            <div className="px-5 py-3.5 border-b border-slate-800 flex items-center justify-between flex-wrap gap-2">
              <span className="font-display font-semibold">{symbol} · Price Chart</span>
              <div className="flex items-center gap-3">
                {chartLast && <span className="font-mono text-lg font-semibold text-white" data-testid="sim-chart-price">{chartLast.c}</span>}
                {chartData && (
                  <span className="flex items-center gap-1.5 text-xs font-mono text-slate-500">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 live-dot" />
                    {chartData.source === "coingecko" ? "Live · CoinGecko" : "Simulated"}
                  </span>
                )}
              </div>
            </div>
            <div className="px-2 py-3">
              <CandleChart candles={chartData?.candles || []} height={300} showVolume={false} />
            </div>
          </div>

          <div className="bg-[#0F172A] border border-slate-800 rounded-2xl overflow-hidden">
            <div className="px-5 py-3.5 border-b border-slate-800 font-display font-semibold">Open Positions</div>
            {account?.positions?.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="sim-positions-table">
                  <thead className="text-xs uppercase tracking-wider text-slate-500 font-mono">
                    <tr className="border-b border-slate-800">
                      <th className="text-left px-5 py-2.5 font-medium">Symbol</th>
                      <th className="text-left px-3 py-2.5 font-medium">Side</th>
                      <th className="text-right px-3 py-2.5 font-medium">Qty</th>
                      <th className="text-right px-3 py-2.5 font-medium">Entry</th>
                      <th className="text-right px-3 py-2.5 font-medium">Now</th>
                      <th className="text-right px-3 py-2.5 font-medium">PnL</th>
                      <th className="px-3 py-2.5" />
                    </tr>
                  </thead>
                  <tbody className="font-mono">
                    {account.positions.map((p) => (
                      <tr key={p.id} className="border-b border-slate-800/60 last:border-0">
                        <td className="px-5 py-3 font-semibold text-slate-100">{p.symbol}</td>
                        <td className={`px-3 py-3 font-semibold ${p.side === "long" ? "text-bull" : "text-bear"}`}>{p.side}</td>
                        <td className="text-right px-3 py-3 text-slate-300">{p.quantity}</td>
                        <td className="text-right px-3 py-3 text-slate-300">{p.entry_price}</td>
                        <td className="text-right px-3 py-3 text-slate-300">{p.current_price}</td>
                        <td className={`text-right px-3 py-3 font-semibold ${p.pnl >= 0 ? "text-bull" : "text-bear"}`}>{p.pnl >= 0 ? "+" : ""}{p.pnl}</td>
                        <td className="px-3 py-3 text-right">
                          <button data-testid={`sim-close-${p.id}`} onClick={() => closePosition(p.id)} className="text-slate-500 hover:text-red-400"><X className="w-4 h-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="px-5 py-8 text-center text-slate-500 text-sm">No open positions. Place an order to start.</div>
            )}
          </div>

          {history && (
            <div className="bg-[#0F172A] border border-slate-800 rounded-2xl overflow-hidden">
              <div className="px-5 py-3.5 border-b border-slate-800 flex items-center justify-between">
                <span className="font-display font-semibold">Performance & History</span>
                <div className="flex gap-4 text-xs font-mono">
                  <span className="text-slate-400">Trades <span className="text-slate-100">{history.stats.total}</span></span>
                  <span className="text-slate-400">Win rate <span className="text-blue-400">{history.stats.win_rate}%</span></span>
                  <span className="text-slate-400">Net <span className={history.stats.total_pnl >= 0 ? "text-bull" : "text-bear"}>{history.stats.total_pnl >= 0 ? "+" : ""}${fmt(history.stats.total_pnl)}</span></span>
                </div>
              </div>
              {history.trades.length ? (
                <div className="overflow-x-auto max-h-80 overflow-y-auto">
                  <table className="w-full text-sm" data-testid="sim-history-table">
                    <thead className="text-xs uppercase tracking-wider text-slate-500 font-mono sticky top-0 bg-[#0F172A]">
                      <tr className="border-b border-slate-800">
                        <th className="text-left px-5 py-2.5 font-medium">Symbol</th>
                        <th className="text-left px-3 py-2.5 font-medium">Side</th>
                        <th className="text-right px-3 py-2.5 font-medium">Entry</th>
                        <th className="text-right px-3 py-2.5 font-medium">Exit</th>
                        <th className="text-right px-3 py-2.5 font-medium">Return</th>
                        <th className="text-right px-5 py-2.5 font-medium">PnL</th>
                      </tr>
                    </thead>
                    <tbody className="font-mono">
                      {history.trades.map((t) => (
                        <tr key={t.id} className="border-b border-slate-800/60 last:border-0">
                          <td className="px-5 py-2.5 font-semibold text-slate-100">{t.symbol}</td>
                          <td className={`px-3 py-2.5 ${t.side === "long" ? "text-bull" : "text-bear"}`}>{t.side}</td>
                          <td className="text-right px-3 py-2.5 text-slate-300">{t.entry_price}</td>
                          <td className="text-right px-3 py-2.5 text-slate-300">{t.exit_price}</td>
                          <td className={`text-right px-3 py-2.5 ${t.return_pct >= 0 ? "text-bull" : "text-bear"}`}>{t.return_pct >= 0 ? "+" : ""}{t.return_pct}%</td>
                          <td className={`text-right px-5 py-2.5 font-semibold ${t.pnl >= 0 ? "text-bull" : "text-bear"}`}>{t.pnl >= 0 ? "+" : ""}{t.pnl}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="px-5 py-8 text-center text-slate-500 text-sm">No closed trades yet.</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
